import { useCallback, useRef, useState } from 'react';
import {
  Modal,
  Button,
  Form,
  Alert,
  TextInput,
  Logo,
} from '@insight-health-ai/roseate/components';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useAuth } from 'src/hooks/auth';
import { useConfigStore } from 'src/hooks/settings';
import ValidateOtp from 'src/components/validateOtp';
import { analytics } from 'src/services/analytics';
import { useMask } from '@react-input/mask';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { i18n } from 'src/services/i18n';

const PatientAuthForm: React.FC<{
  chatToken: string;
}> = ({ chatToken }) => {
  const { error: authError, validateAnonymousUser } = useAuth();
  const { config } = useConfigStore();
  const [isLoading, setIsLoading] = useState(false);
  const [isOtpModalOpen, setIsOtpModalOpen] = useState(false);

  const mode = config.mode === 'form' ? 'form' : 'chat';
  const { t } = useTranslation('auth', { keyPrefix: 'patientAuth' });
  const isLocaleChanged = useRef(false);

  /**
   * @todo Harshit: Fix typings here
   */
  const onSubmit = useCallback(async ({ dateOfBirth }: any) => {
    setIsLoading(true);
    try {
      const date = format(new Date(dateOfBirth), 'yyyy-MM-dd');
      await validateAnonymousUser({
        dateOfBirth: date,
        chatToken,
        preferredLanguage: isLocaleChanged.current ? i18n.language : undefined,
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleOtpSubmit = useCallback(async (otp: string) => {
    setIsLoading(true);
    try {
      await validateAnonymousUser({
        otp,
        chatToken,
        preferredLanguage: isLocaleChanged.current ? i18n.language : undefined,
      });
      setIsOtpModalOpen(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const inputRef = useMask({
    mask: '__/__/____',
    replacement: { _: /\d/ },
    showMask: true,
  });

  return (
    <>
      <Modal
        open={true}
        closable={false}
        width={520}
        footer={null}
        title={
          <div className="flex items-center justify-center">
            <Logo className="h-8" />
          </div>
        }
      >
        <div className="flex flex-col gap-4">
          <Alert
            appearance="success"
            message={t(`alert.${mode}.title`)}
            icon={<InfoCircleOutlined />}
            description={t(`alert.${mode}.description`)}
          />
          <Form onFinish={onSubmit}>
            <Form.Item
              label={t('dob.label')}
              name="dateOfBirth"
              // @ts-ignore
              validateTrigger={['onBlur', 'onSubmit']}
              rules={[
                { required: true, message: t('dob.requiredError') },
                {
                  pattern: /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/,
                  message: t('dob.formatError'),
                },
              ]}
            >
              <TextInput
                inputMode="numeric"
                size="large"
                placeholder={t('dob.placeholder')}
                ref={(el) => {
                  if (el) {
                    inputRef.current = el?.nativeElement as any;
                  }
                }}
              />
            </Form.Item>
            <Form.Footer>
              <Button
                block
                size="lg"
                type="submit"
                loading={isLoading}
                disabled={isLoading}
              >
                {t('submit')}
              </Button>

              {authError && (
                <div className="flex flex-col gap-2 items-center justify-center text-center mt-2">
                  <div className="text-xs text-danger">{authError}</div>
                  {/* @todo Harshit: replace with link / text button */}
                  <div
                    style={{ color: '#0070f3' }}
                    className="underline text-blue-500 cursor-pointer hover:bg-gray-50 active:bg-gray-50 rounded-lg py-2 px-3"
                    onClick={() => {
                      analytics.track('Clicked Having Trouble Logging In', {});
                      setIsOtpModalOpen(true);
                    }}
                  >
                    {t('troubleLoggingIn')}
                  </div>
                </div>
              )}
              <div className="text-xs text-danger text-center mt-6">
                {t('emergencyGuideline')}
              </div>
              <div
                className="text-center mt-4 cursor-pointer underline"
                onClick={() => {
                  const toggleLanguage =
                    i18n.language === 'en-US' ? 'es-ES' : 'en-US';
                  analytics.track('Changed Language While Logging In', {
                    from: i18n.language,
                    to: toggleLanguage,
                  });
                  isLocaleChanged.current = true;
                  i18n.changeLanguage(toggleLanguage);
                }}
              >
                {t('languageToggle')}
              </div>
            </Form.Footer>
          </Form>
        </div>
      </Modal>
      {isOtpModalOpen && (
        <ValidateOtp
          open={true}
          onSubmit={handleOtpSubmit}
          onClose={() => setIsOtpModalOpen(false)}
        />
      )}
    </>
  );
};

export { PatientAuthForm };
